import { ref } from 'vue';
import type { Ref } from 'vue';

import { usePurchaseOrderStore } from '@/store/purchaseOrderStore';
import type { PurchaseOrder } from '@/types/purchaseOrders';

// Line items come from the Odoo-backed PO read, which only serves confirmed and locked POs.
const LINE_ITEM_STATES = ['purchase', 'done'];

export interface BoardLineItems {
  po: Ref<PurchaseOrder | null>;
  loading: Ref<boolean>;
  error: Ref<string | null>;
  open: (poNumber: string, vendorId: string) => Promise<void>;
  close: () => void;
  clearError: () => void;
}

// The scheduling board holds schedule groups, not purchase orders — line items live only on
// the Odoo-backed PO read, so they are fetched when asked for. Going through the store means
// a repeat open of the same PO is served from its 10-minute cache.
export function useBoardLineItems(): BoardLineItems {
  const poStore = usePurchaseOrderStore();

  const po = ref<PurchaseOrder | null>(null);
  const loading = ref(false);
  const error = ref<string | null>(null);

  async function open(poNumber: string, vendorId: string): Promise<void> {
    error.value = null;
    loading.value = true;
    poStore.states = [...LINE_ITEM_STATES];
    poStore.schedulableForType = undefined;
    poStore.search = poNumber;
    await poStore.load(vendorId);
    loading.value = false;

    if (poStore.error) {
      error.value = poStore.error;
      return;
    }
    const found = poStore.items.find((item) => item.poNumber === poNumber) ?? null;
    if (!found) {
      error.value = `${poNumber} is no longer available from Odoo.`;
      return;
    }
    po.value = found;
  }

  function close(): void {
    po.value = null;
  }

  function clearError(): void {
    error.value = null;
  }

  return { po, loading, error, open, close, clearError };
}
